import React from "react"
import { Button } from "@/components/ui/button"
import { AlignLeft, Clock, User, Calendar, Folder } from "lucide-react"
import { testIds } from "@/lib/test-ids"
import { PersonLink } from "@/components/features/person-link"
import { getDocumentIcon, isProjectRootFile } from "@/lib/document-icons"
import type { Document, DocumentType, ExplorerFolder } from "./document-list.types"
import { getListTypeLabel } from "./document-list.labels"
import {
  DOCUMENT_LIST_COLS_CLASS,
  folderColumnHint,
  folderContentTypeLine,
  folderCountLine,
  folderTagLine,
  formatFileSize,
  getDocumentFilename,
  isReadmeDocument,
  stripHtmlTags,
} from "./document-list.utils"
import { DocumentTileMarkdown } from "./document-tile-markdown"

function formatDocumentStatus(status: string): string {
  return status
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ")
}

export function LoadMoreTrigger({
  ref,
  loading,
  hasMore,
  error,
  onRetry,
  currentCount,
  totalCount,
  "data-testid": testId,
}: {
  ref: React.RefObject<HTMLDivElement | null>
  loading: boolean
  hasMore: boolean
  error: string | null
  onRetry: () => void
  currentCount: number
  totalCount: number
  "data-testid"?: string
}) {
  if (!hasMore) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <p className="text-sm">
          All documents loaded ({currentCount} of {totalCount})
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-destructive text-sm mb-3">Failed to load more documents</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try Again
        </Button>
      </div>
    )
  }

  if (loading) {
    return (
      <div ref={ref} className="p-6" data-testid={testId} aria-label="Loading more documents">
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse border border-border rounded-lg p-4">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-muted rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return <div ref={ref} className="h-4" />
}

function getTypeLabel(type: DocumentType | string) {
  return getListTypeLabel(type) || "Note"
}

function DocumentFilename({ document }: { document: Document }) {
  const filename = getDocumentFilename(document)
  if (!filename) return null
  const isReadme = isReadmeDocument(document)

  return (
    <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
      <div
        className={
          isReadme
            ? "truncate font-mono text-xs font-semibold text-foreground/90"
            : "truncate font-mono text-[11px] text-muted-foreground/75"
        }
        title={filename}
      >
        {filename}
      </div>
      {isReadme ? (
        <span className="shrink-0 rounded border border-border bg-muted px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground/80">
          Index
        </span>
      ) : null}
    </div>
  )
}

function ListTypeChip({ label }: { label: string }) {
  if (!label) return null
  return (
    <span className="inline-flex max-w-full truncate rounded-md bg-muted/50 px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
      {label}
    </span>
  )
}

export function DocumentCard({
  document,
  onClick,
  onDoubleClick,
  onContextMenu,
  isActive = false,
  isSelected = false,
  innerRef,
  "data-testid": testId,
}: {
  document: Document
  onClick?: () => void
  onDoubleClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onContextMenu?: (document: Document, event: React.MouseEvent) => void
  isActive?: boolean
  isSelected?: boolean
  innerRef?: React.RefObject<HTMLElement | null>
  "data-testid"?: string
}) {
  const isProjectRoot = isProjectRootFile(document.filePath || document.path)
  const isReadme = isReadmeDocument(document)

  return (
    <button
      ref={innerRef as React.Ref<HTMLButtonElement>}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={
        onContextMenu ? (e) => {
          e.preventDefault()
          onContextMenu(document, e)
        } : undefined
      }
      draggable
      onPointerDown={dragOnPrimaryButton}
      onDragStart={(e) => {
        e.dataTransfer.setData(
          "application/json",
          JSON.stringify({
            id: document.id,
            title: document.title,
            documentType: document.type,
          }),
        )
        e.dataTransfer.effectAllowed = "copy"
      }}
      className={`w-full p-4 border rounded-lg hover:shadow-sm transition-all text-left group relative ${
        isSelected
          ? "ring-2 ring-primary bg-primary/5 border-primary"
          : isProjectRoot
            ? "border-l-4 border-l-blue-500 border-t-border border-r-border border-b-border bg-blue-500/5 hover:bg-blue-500/10 hover:border-blue-500"
            : isReadme
              ? "border-l-4 border-l-foreground/40 border-t-border border-r-border border-b-border bg-muted/20 hover:bg-muted/35 hover:border-foreground/30"
              : isActive
              ? "border-l-4 border-l-green-500 border-t-border border-r-border border-b-border bg-green-500/5 hover:bg-green-500/10 hover:border-green-500"
              : "border-border hover:border-primary bg-card"
      }`}
      aria-label={`${document.title}, ${getTypeLabel(document.type)}${isReadme ? ", folder index" : ""}`}
      aria-selected={isSelected}
      data-testid={testId}
    >
      {isActive && !isProjectRoot && (
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 rounded-full bg-green-500 text-white text-[10px] font-semibold">
            VIEWING
          </span>
        </div>
      )}

      {isProjectRoot && (
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 rounded-full bg-blue-500 text-white text-[10px] font-semibold">
            PROJECT ROOT
          </span>
        </div>
      )}

      {!isProjectRoot && isReadme && (
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 rounded-full bg-foreground/80 text-background text-[10px] font-semibold">
            INDEX
          </span>
        </div>
      )}

      <div className="flex items-start gap-3 mb-2">
        {getDocumentIcon(document.type, isProjectRoot ? "h-5 w-5" : "h-5 w-5", isProjectRoot)}
        <div className="flex-1 min-w-0 pr-20">
          <h3
            className={`text-base font-semibold transition-colors truncate ${
              isProjectRoot
                ? "text-blue-700 dark:text-blue-400 group-hover:text-blue-800 dark:group-hover:text-blue-300"
                : "text-card-foreground group-hover:text-primary"
            }`}
          >
            {document.title}
          </h3>
          <DocumentFilename document={document} />
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2 ml-8">
        <span className="font-medium">{getTypeLabel(document.type)}</span>
        <span>•</span>
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Updated {document.updatedAt}
        </span>
        <span>•</span>
        <span className="flex items-center gap-1">
          <User className="h-3 w-3" />
          <PersonLink name={document.updatedBy} userId={document.updatedByUserId ?? undefined} />
        </span>
      </div>

      {document.tags.length > 0 && (
        <div className="flex items-center gap-1.5 mb-3 ml-8 flex-wrap">
          {document.tags.map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <p className="text-sm text-muted-foreground line-clamp-2 ml-8 mb-3">
        {stripHtmlTags(document.excerpt || "")}
      </p>

      <div className="ml-8 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {document.lineCount != null && document.lineCount > 0 && (
          <span className="flex items-center gap-1 tabular-nums">
            <AlignLeft className="h-3 w-3 shrink-0" aria-hidden />
            {document.lineCount.toLocaleString()} lines
          </span>
        )}
        {document.status && (
          <span className="text-foreground/85 font-medium">
            {formatDocumentStatus(String(document.status))}
          </span>
        )}
        {document.dueDate && (
          <>
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3 shrink-0" />
              Due: {document.dueDate}
            </span>
          </>
        )}
        {document.assignee && (
          <>
            <span className="flex items-center gap-1">
              <User className="h-3 w-3 shrink-0" />
              Assignee: {document.assignee}
            </span>
          </>
        )}
        {document.duration && (
          <>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3 shrink-0" />
              Duration: {document.duration}
            </span>
          </>
        )}
      </div>
    </button>
  )
}

/** Tracks the list column, not the window, so an open preview does not crush cards. */
export const FOLDER_CARD_GRID_CLASS =
  'grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(15.5rem,1fr))]'

export function DocumentGridCard({
  document,
  onClick,
  onDoubleClick,
  onContextMenu,
  isActive = false,
  isSelected = false,
  innerRef,
  "data-testid": testId,
}: {
  document: Document
  onClick?: () => void
  onDoubleClick?: (e: React.MouseEvent<HTMLElement>) => void
  onContextMenu?: (document: Document, event: React.MouseEvent) => void
  isActive?: boolean
  isSelected?: boolean
  innerRef?: React.RefObject<HTMLElement | null>
  "data-testid"?: string
}) {
  const isProjectRoot = isProjectRootFile(document.filePath || document.path)
  const isReadme = isReadmeDocument(document)
  const author = document.updatedBy?.trim() || "Unknown"
  const initials = tileInitials(author)

  return (
    <div
      ref={innerRef as React.Ref<HTMLDivElement>}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onClick?.()
        }
      }}
      onContextMenu={
        onContextMenu ? (e) => {
          e.preventDefault()
          onContextMenu(document, e)
        } : undefined
      }
      draggable
      onPointerDown={dragOnPrimaryButton}
      onDragStart={(e) => {
        e.dataTransfer.setData(
          "application/json",
          JSON.stringify({
            id: document.id,
            title: document.title,
            documentType: document.type,
          }),
        )
        e.dataTransfer.effectAllowed = "copy"
      }}
      className={`group relative flex h-72 flex-col overflow-hidden rounded-lg border bg-card p-3 text-left shadow-sm transition-all hover:border-primary/40 hover:shadow-md ${
        isSelected
          ? "border-primary ring-2 ring-primary"
          : isProjectRoot
            ? "border-blue-500/40"
            : isReadme
              ? "border-foreground/20"
              : isActive
                ? "border-green-500/50"
                : "border-border"
      }`}
      aria-label={`${document.title}, ${getTypeLabel(document.type)}${isReadme ? ", folder index" : ""}`}
      data-testid={testId}
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground"
          aria-hidden
        >
          {initials}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium text-foreground">{author}</span>
          <span className="block truncate text-[11px] text-muted-foreground">
            {document.updatedAt ? `Edited ${document.updatedAt}` : ""}
          </span>
        </span>
        <DocumentSizeBadges document={document} />
      </div>
      <h3 className="mb-1 line-clamp-2 text-sm font-semibold text-card-foreground">{document.title}</h3>
      <div className="min-h-0 flex-1 overflow-hidden [mask-image:linear-gradient(to_bottom,black_68%,transparent)]">
        <DocumentTileMarkdown
          source={document.cardMarkdown || document.excerpt || ""}
          title={document.title}
        />
      </div>
      {isActive && !isProjectRoot ? (
        <span className="absolute right-2 top-2 rounded-full bg-green-500 px-1.5 py-0.5 text-[9px] font-semibold text-white">
          VIEWING
        </span>
      ) : null}
    </div>
  )
}

function DocumentSizeBadges({ document }: { document: Document }) {
  const lines = document.lineCount != null && document.lineCount > 0 ? document.lineCount : null
  const size = formatFileSize(document.byteSize)
  if (lines == null && !size) return null
  return (
    <span className="flex shrink-0 flex-col items-end gap-0.5">
      {lines != null ? (
        <span
          className="rounded bg-muted px-1 py-px text-[10px] font-medium tabular-nums text-muted-foreground"
          title={`${lines} lines`}
        >
          {lines.toLocaleString()} lines
        </span>
      ) : null}
      {size ? (
        <span
          className="rounded bg-muted px-1 py-px text-[10px] font-medium tabular-nums text-muted-foreground"
          title={`${document.byteSize} bytes`}
        >
          {size}
        </span>
      ) : null}
    </span>
  )
}

/** Left-button drags only. A right-click on a draggable row otherwise swallows contextmenu. */
function dragOnPrimaryButton(event: React.PointerEvent<HTMLElement>) {
  event.currentTarget.draggable = event.button === 0
}

function tileInitials(name: string): string {
  const parts = name.split(/[\s@._-]+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

export function DocumentListRow({
  document,
  onClick,
  onDoubleClick,
  onContextMenu,
  isSelected = false,
  urlCollision = false,
}: {
  document: Document
  onClick?: () => void
  onDoubleClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onContextMenu?: (document: Document, event: React.MouseEvent) => void
  isSelected?: boolean
  urlCollision?: boolean
}) {
  const isProjectRoot = isProjectRootFile(document.filePath || document.path)
  const isReadme = isReadmeDocument(document)
  const typeLabel = getListTypeLabel(document.type)

  return (
    <button
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={
        onContextMenu ? (e) => {
          e.preventDefault()
          onContextMenu(document, e)
        } : undefined
      }
      draggable
      onPointerDown={dragOnPrimaryButton}
      onDragStart={(e) => {
        e.dataTransfer.setData(
          "application/json",
          JSON.stringify({
            id: document.id,
            title: document.title,
            documentType: document.type,
          }),
        )
        e.dataTransfer.effectAllowed = "copy"
      }}
      className={`${DOCUMENT_LIST_COLS_CLASS} py-3 border-b border-border/40 transition-colors text-left ${
        isSelected
          ? "bg-primary/8 border-l-2 border-l-primary pl-[calc(0.75rem-2px)]"
          : isProjectRoot
            ? "bg-blue-500/5 border-l-2 border-l-blue-500 pl-[calc(0.75rem-2px)] hover:bg-muted/40"
            : isReadme
              ? "bg-muted/25 border-l-2 border-l-foreground/35 pl-[calc(0.75rem-2px)] hover:bg-muted/40"
              : "hover:bg-muted/40"
      }`}
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <div className="mt-0.5 flex-shrink-0 text-muted-foreground">
          {getDocumentIcon(document.type, "h-4 w-4", isProjectRoot)}
        </div>
        <div className="min-w-0 flex-1">
          <div
            className={`truncate text-sm ${
              isReadme ? "font-semibold text-foreground" : "font-medium text-foreground"
            }`}
          >
            {document.title}
          </div>
          <DocumentFilename document={document} />
          {urlCollision && (
            <div
              className="mt-0.5 text-xs text-amber-600 dark:text-amber-400"
              data-testid={testIds.documentList.urlCollision}
            >
              Same URL as a folder with this name - move this file to README.md
            </div>
          )}
          {document.excerpt && (
            <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground/80">
              {stripHtmlTags(document.excerpt)}
            </div>
          )}
        </div>
      </div>

      <div className="hidden min-w-0 justify-self-end sm:block">
        <ListTypeChip label={typeLabel} />
      </div>

      <span className="justify-self-end text-right text-xs tabular-nums text-muted-foreground">
        {document.updatedAt}
      </span>

      <span className="hidden justify-self-end text-right text-xs tabular-nums text-muted-foreground md:block">
        {document.lineCount != null && document.lineCount > 0
          ? document.lineCount.toLocaleString()
          : ""}
      </span>

      <span className="hidden justify-self-end text-right text-xs tabular-nums text-muted-foreground md:block">
        {formatFileSize(document.byteSize)}
      </span>

      <span className="hidden min-w-0 justify-self-end truncate text-right text-xs md:block">
        {document.updatedBy ? (
          <PersonLink
            name={document.updatedBy}
            userId={document.updatedByUserId ?? undefined}
            className="text-muted-foreground hover:text-foreground"
          />
        ) : (
          <span className="text-muted-foreground/50">-</span>
        )}
      </span>
    </button>
  )
}

export function FolderCard({
  folder,
  onClick,
  onDoubleClick,
  onContextMenu,
  color,
  isSelected = false,
}: {
  folder: { name: string; path: string }
  onClick: () => void
  onDoubleClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  onContextMenu?: (folder: { name: string; path: string }, event: React.MouseEvent) => void
  color?: string
  isSelected?: boolean
}) {
  return (
    <button
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={
        onContextMenu ? (e) => {
          e.preventDefault()
          onContextMenu(folder, e)
        } : undefined
      }
      className={`w-full p-3 border rounded-lg transition-all text-left group ${
        isSelected
          ? "ring-2 ring-primary bg-primary/5 border-primary"
          : "border-border hover:border-primary hover:shadow-sm bg-card"
      }`}
    >
      <div className="flex items-center gap-2">
        <Folder className="h-5 w-5 flex-shrink-0" style={{ color: color ? color + "bb" : undefined }} />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-card-foreground group-hover:text-primary truncate">
            {folder.name}
          </h3>
        </div>
      </div>
    </button>
  )
}

function FolderMetaLines({ folder }: { folder: ExplorerFolder }) {
  const count = folderCountLine(folder)
  const types = folderContentTypeLine(folder.contentTypes)
  const tags = folderTagLine(folder.tags)
  if (!count && !types && !tags) return null
  return (
    <div className="mt-0.5 space-y-0.5">
      {count ? <p className="truncate text-[11px] text-muted-foreground">{count}</p> : null}
      {types ? <p className="truncate text-[11px] text-muted-foreground">{types}</p> : null}
      {tags ? <p className="truncate text-[11px] text-muted-foreground">{tags}</p> : null}
    </div>
  )
}

export function FolderGridCard({
  folder,
  onClick,
  onDoubleClick,
  onContextMenu,
  color,
  isSelected = false,
}: {
  folder: ExplorerFolder
  onClick: () => void
  onDoubleClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  onContextMenu?: (folder: { name: string; path: string }, event: React.MouseEvent) => void
  color?: string
  isSelected?: boolean
}) {
  return (
    <button
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={
        onContextMenu ? (e) => {
          e.preventDefault()
          onContextMenu(folder, e)
        } : undefined
      }
      className={`group relative h-full w-full p-2.5 border rounded-lg transition-all text-left flex items-start gap-2 ${
        isSelected
          ? "ring-2 ring-primary bg-primary/5 border-primary"
          : "border-border hover:border-primary hover:shadow-md bg-card"
      }`}
    >
      <div className="mt-0.5 flex-shrink-0">
        <Folder className="h-4 w-4" style={{ color: color ? color + "bb" : undefined }} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold truncate text-card-foreground">{folder.name}</h3>
        <FolderMetaLines folder={folder} />
      </div>
    </button>
  )
}

export function FolderListRow({
  folder,
  onClick,
  onDoubleClick,
  onContextMenu,
  color,
  isSelected = false,
  updatedAtLabel,
  urlCollision = false,
}: {
  folder: ExplorerFolder
  onClick: () => void
  onDoubleClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  onContextMenu?: (folder: { name: string; path: string }, event: React.MouseEvent) => void
  color?: string
  isSelected?: boolean
  /** Human-readable modified time (e.g. from formatTimeAgo). */
  updatedAtLabel?: string
  urlCollision?: boolean
}) {
  const hint = folderColumnHint(folder)
  const count = folderCountLine(folder)
  const fileLabel = typeof folder.fileCount === "number" ? String(folder.fileCount) : ""
  const folderLabel = typeof folder.folderCount === "number" ? String(folder.folderCount) : ""
  return (
    <button
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={
        onContextMenu ? (e) => {
          e.preventDefault()
          onContextMenu(folder, e)
        } : undefined
      }
      className={`${DOCUMENT_LIST_COLS_CLASS} py-3 border-b border-border/40 transition-colors text-left ${
        isSelected
          ? "bg-primary/8 border-l-2 border-l-primary pl-[calc(0.75rem-2px)]"
          : "hover:bg-muted/40"
      }`}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <Folder
          className="h-4 w-4 flex-shrink-0 text-muted-foreground"
          style={{ color: color ? color + "bb" : undefined }}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">
            {folder.name}
          </div>
          {count ? (
            <div className="mt-0.5 truncate text-xs text-muted-foreground">{count}</div>
          ) : null}
          {urlCollision && (
            <div
              className="mt-0.5 text-xs text-amber-600 dark:text-amber-400"
              data-testid={testIds.documentList.urlCollision}
            >
              Same URL as a sibling file - that file should be this folder&apos;s README
            </div>
          )}
        </div>
      </div>
      <div className="hidden min-w-0 justify-self-end sm:block">
        <ListTypeChip label="Folder" />
      </div>
      <span className="justify-self-end text-right text-xs tabular-nums text-muted-foreground">
        {updatedAtLabel ?? ""}
      </span>
      <span
        className="hidden justify-self-end truncate text-right text-xs text-muted-foreground md:block"
        title={typeof folder.fileCount === "number" ? `${fileLabel} files` : undefined}
      >
        {fileLabel}
      </span>
      <span
        className="hidden justify-self-end truncate text-right text-xs text-muted-foreground md:block"
        title={typeof folder.folderCount === "number" ? `${folderLabel} folders` : undefined}
      >
        {folderLabel}
      </span>
      <span className="hidden min-w-0 justify-self-end truncate text-right text-xs text-muted-foreground md:block">
        {hint}
      </span>
    </button>
  )
}
